import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import React, { Dispatch, SetStateAction, useState } from "react";
import LogoutOverlay from "../user/LogoutOverlay";
import logoutLogo from "../../assets/icons/Icon-logout.svg";
import orderLogo from "../../assets/icons/Icon-orders.svg";
import userLogo from "../../assets/icons/Icon-user.svg";
import lineOfCreditLogo from "../../assets/icons/Icon-LOC1.svg";
import walletLogo from "../../assets/icons/Icon-wallet.svg";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { logout, setFirstLogin } from "@/store/slices/authSlice";
import tourLogo from "../../assets/icons/tourIcon.svg";
import { StaticImport } from "next/dist/shared/lib/get-img-props";

interface PopOverProps {
  anchorEl: HTMLElement | null;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}


const PopOver: React.FC<PopOverProps> = ({ anchorEl, setAnchorEl }) => {
  const dispatch = useAppDispatch();
  const [openLogout, setOpenLogout] = useState<boolean>(false);
  const { availableCredit } = useAppSelector((state) => state.payment);
  const { bulkUserID, isAuthenticated } = useAppSelector((state) => state.auth);

  const profileOpen = Boolean(anchorEl);

  const handleOpenLogout = () => setOpenLogout(true);
  const handleCloseLogout = () => setOpenLogout(false);
  const handleConfirmLogout = () => {
    dispatch(logout());
    setOpenLogout(false);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleProfileClose = () => setAnchorEl(null);

  const getLinkPath = (path: string) => {
    if (!isAuthenticated) return "/auth";
    if (isAuthenticated && !bulkUserID) return "/onboarding";
    return path;
  };

  return (
    <Popover
      open={profileOpen}
      anchorEl={anchorEl}
      onClose={handleProfileClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{ mt: 2 }}
    >
      <Box
        sx={{
          width: "content-fit",
          px:1,
          borderRadius: "10px",
          backgroundColor: "white",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <List>
          {[
            { text: "My Profile", icon: userLogo, path: "/user/profile" },
            { text: "My Orders", icon: orderLogo, path: "/user/orders" },
            availableCredit?.status && {
              text: "Line of Credit",
              icon: lineOfCreditLogo,
              path: "/user/lineofcredit",
            },
            { text: "Wallet", icon: walletLogo, path: "/user/wallet" },
          ]
            .filter((item): item is { text: string; icon: StaticImport; path: string } =>
              Boolean(item)
            )
            .map((item, index) => (
              <Link key={index} href={getLinkPath(item.path)} passHref>
                <ListItem
                  component="button"
                  sx={{
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    borderRadius: "4px",
                    paddingY:"6px"
                  }}
                  onClick={handleProfileClose}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Image
                      src={item.icon}
                      alt={item.text}
                      width={24}
                      height={24}
                      style={{ borderRadius: "50%" }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              </Link>
            ))}

          <ListItem
            component="button"
            onClick={() => {
              handleProfileClose();
              dispatch(setFirstLogin(true));
            }}
            sx={{
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
              borderRadius: "4px",
              paddingY:"6px"
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Image
                src={tourLogo}
                alt="Tour"
                width={24}
                height={24}
                style={{ borderRadius: "50%" }}
              />
            </ListItemIcon>
            <ListItemText primary="Take a Tour" />
          </ListItem>

          <ListItem
            component="button"
            onClick={handleOpenLogout}
            sx={{
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
              borderRadius: "4px",
              paddingY:"6px"
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Image
                src={logoutLogo}
                alt="Logout"
                width={24}
                height={24}
                style={{ borderRadius: "50%" }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              sx={{ color: "var(--color-peach)" }}
            />
          </ListItem>
        </List>
        <LogoutOverlay
          open={openLogout}
          onClose={handleCloseLogout}
          onConfirm={handleConfirmLogout}
        />
      </Box>
    </Popover>
  );
};

export default PopOver;
